/**
 * No-Data Communications Report utilities.
 * Derives silent assets from fleet lastUpdated timestamps.
 */

import { getDateOffset } from "@/lib/datetime-utils.js";

const MS_PER_HOUR = 60 * 60 * 1000;
const MS_PER_DAY = 24 * MS_PER_HOUR;

const MS_24H = MS_PER_DAY;
const MS_7D = 7 * MS_PER_DAY;
const MS_30D = 30 * MS_PER_DAY;

/** @param {string} selectedRange @param {string} [fallback] */
function normalizePresetRange(selectedRange, fallback = "24h") {
  return selectedRange && selectedRange !== "custom" ? selectedRange : fallback;
}

/** @param {unknown} value @returns {number|null} */
function finiteNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * @param {string} dateString - YYYY-MM-DD
 * @param {boolean} isEndOfDay
 * @returns {Date}
 */
function createDateFromCustomInput(dateString, isEndOfDay = false) {
  const [year, month, day] = dateString.split("-").map(Number);
  return isEndOfDay
    ? new Date(year, month - 1, day, 23, 59, 59, 999)
    : new Date(year, month - 1, day, 0, 0, 0, 0);
}

/**
 * @param {string} selectedRange - "24h" | "7d" | "30d" | "custom"
 * @param {string} customStart - YYYY-MM-DD
 * @param {string} customEnd - YYYY-MM-DD
 * @param {Date} now
 * @returns {{ startCutoff: Date, endCutoff: Date, rangeLabel: string }}
 */
export function getCutoffsForRange(selectedRange, customStart, customEnd, now) {
  if (selectedRange === "custom" && customStart && customEnd) {
    return {
      startCutoff: createDateFromCustomInput(customStart),
      endCutoff: createDateFromCustomInput(customEnd, true),
      rangeLabel: "custom",
    };
  }

  const range = normalizePresetRange(selectedRange);
  return {
    startCutoff: new Date(0),
    endCutoff: getDateOffset(now.toISOString(), range),
    rangeLabel: range,
  };
}

/**
 * UTC date range for POST /api/assets/no-data-stats (bounded window semantics).
 * @param {string} selectedRange - "24h" | "7d" | "30d" | "custom"
 * @param {string} customStart - YYYY-MM-DD
 * @param {string} customEnd - YYYY-MM-DD
 * @param {Date} now
 * @returns {{ FromDate: string, ToDate: string } | null}
 */
export function getNoDataStatsDateRange(
  selectedRange,
  customStart,
  customEnd,
  now
) {
  if (selectedRange === "custom") {
    if (!customStart || !customEnd) return null;
    return {
      FromDate: createDateFromCustomInput(customStart).toISOString(),
      ToDate: createDateFromCustomInput(customEnd, true).toISOString(),
    };
  }

  const range = normalizePresetRange(selectedRange);
  return {
    FromDate: getDateOffset(now.toISOString(), range).toISOString(),
    ToDate: now.toISOString(),
  };
}

/**
 * Stable cache key for no-data stats (preset ranges ignore wall-clock drift).
 * @param {string} selectedRange - "24h" | "7d" | "30d" | "custom"
 * @param {string} customStart - YYYY-MM-DD
 * @param {string} customEnd - YYYY-MM-DD
 * @returns {string | null}
 */
export function buildNoDataStatsCacheKey(
  selectedRange,
  customStart,
  customEnd,
  assetId
) {
  const assetSuffix =
    assetId != null && assetId !== "" ? `:asset:${assetId}` : "";

  if (selectedRange === "custom") {
    if (!customStart || !customEnd) return null;
    const dateRange = getNoDataStatsDateRange(
      selectedRange,
      customStart,
      customEnd,
      new Date()
    );
    if (!dateRange) return null;
    return `${dateRange.FromDate}|${dateRange.ToDate}${assetSuffix}`;
  }

  const range = normalizePresetRange(selectedRange);
  return `preset:${range}${assetSuffix}`;
}

/**
 * Resolve cache key and API date range for a no-data stats request.
 * @param {string} selectedRange
 * @param {string} customStart
 * @param {string} customEnd
 * @param {string|number|undefined} assetId
 * @param {Date} [now]
 * @returns {{ cacheKey: string, dateRange: { FromDate: string, ToDate: string }, assetId: string|number|undefined } | null}
 */
export function buildNoDataStatsQuery(
  selectedRange,
  customStart,
  customEnd,
  assetId,
  now = new Date()
) {
  const cacheKey = buildNoDataStatsCacheKey(
    selectedRange,
    customStart,
    customEnd,
    assetId
  );
  if (!cacheKey) return null;

  const dateRange = getNoDataStatsDateRange(
    selectedRange,
    customStart,
    customEnd,
    now
  );
  if (!dateRange) return null;

  return { cacheKey, dateRange, assetId };
}

/**
 * Resolve stats API range when custom dates may be incomplete.
 * @param {string} selectedRange
 * @param {string} customStart
 * @param {string} customEnd
 * @param {string} previousRange
 * @returns {string}
 */
export function getEffectiveStatsRange(
  selectedRange,
  customStart,
  customEnd,
  previousRange
) {
  if (selectedRange === "custom" && customStart && customEnd) return "custom";
  if (selectedRange === "custom") return previousRange;
  return selectedRange;
}

/**
 * @param {string} range
 * @returns {string}
 */
export function getRangeSubtitle(range) {
  switch (range) {
    case "24h":
      return "Last 24 hours";
    case "7d":
      return "Last 7 days";
    case "custom":
      return "Custom range";
    default:
      return "Last 30 days";
  }
}

/**
 * @param {Object} asset
 * @param {Date} now
 * @returns {number}
 */
export function computeSilentDurationMs(asset, now) {
  if (!asset?.lastUpdated) return 0;
  const last = new Date(asset.lastUpdated);
  if (isNaN(last.getTime())) return 0;
  return Math.max(0, now.getTime() - last.getTime());
}

/**
 * Assets whose last telemetry update falls within the silent window.
 * @param {Object[]} assets
 * @param {{ startCutoff: Date, endCutoff: Date }} cutoffs
 * @returns {Object[]}
 */
export function filterSilentAssets(assets, cutoffs) {
  if (!Array.isArray(assets)) return [];

  const { startCutoff, endCutoff } = cutoffs;
  if (!endCutoff) return [];

  return assets.filter((asset) => {
    if (!asset?.lastUpdated) return false;
    const last = new Date(asset.lastUpdated);
    if (isNaN(last.getTime())) return false;
    return last.getTime() <= endCutoff.getTime()
      && last.getTime() >= startCutoff.getTime();
  });
}

/**
 * @param {Object[]} silentAssets
 * @param {Date} now
 * @param {string} rangeLabel - selected range for scoped bucketing
 * @returns {{ within24h7d: number, within7d30d: number, beyond30d: number }}
 */
export function bucketSilentAssets(silentAssets, now, rangeLabel = "24h") {
  let within24h7d = 0;
  let within7d30d = 0;
  let beyond30d = 0;

  const windowMs =
    rangeLabel === "7d"
      ? MS_7D
      : rangeLabel === "30d"
        ? MS_30D
        : rangeLabel === "custom"
          ? null
          : MS_24H;

  for (const asset of silentAssets) {
    const silentMs = computeSilentDurationMs(asset, now);

    if (silentMs >= MS_30D) {
      beyond30d++;
    } else if (silentMs >= MS_7D) {
      within7d30d++;
    } else if (silentMs >= MS_24H) {
      within24h7d++;
    }
  }

  if (windowMs === MS_24H) {
    within7d30d = 0;
    beyond30d = 0;
  } else if (windowMs === MS_7D) {
    within24h7d = 0;
    beyond30d = 0;
  } else if (windowMs === MS_30D) {
    within24h7d = 0;
    within7d30d = 0;
  }

  return { within24h7d, within7d30d, beyond30d };
}

/**
 * @param {number} silentMs
 * @returns {{ key: string, label: string, badgeClassName: string }}
 */
export function getSeverity(silentMs) {
  if (silentMs >= MS_30D) {
    return {
      key: "critical30d",
      label: "Critical · 30d+",
      badgeClassName:
        "bg-red-100 text-red-800 border-red-200 hover:bg-red-100",
    };
  }
  if (silentMs >= MS_7D) {
    return {
      key: "critical",
      label: "Critical · 7-30d",
      badgeClassName:
        "bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-100",
    };
  }
  return {
    key: "warn",
    label: "Warning",
    badgeClassName:
      "bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100",
  };
}

/**
 * @param {number} silentMs
 * @returns {string}
 */
function formatSilentDuration(silentMs) {
  if (!Number.isFinite(silentMs) || silentMs <= 0) return "—";
  const hours = silentMs / MS_PER_HOUR;
  if (hours < 24) {
    return `${hours.toFixed(1)}h`;
  }
  const days = silentMs / MS_PER_DAY;
  return `${days.toFixed(1)}d`;
}

/**
 * @param {Object} asset
 * @param {Date} now
 * @returns {Object}
 */
function enrichAssetWithSilentFields(asset, now) {
  const silentDurationMs = computeSilentDurationMs(asset, now);
  const severity = getSeverity(silentDurationMs);
  return {
    ...asset,
    silentDurationMs,
    silentDurationLabel: formatSilentDuration(silentDurationMs),
    severity,
  };
}

/**
 * @param {Object|Object[]|null|undefined} apiResponse
 * @returns {Map<string, { noDataEventCount: number|null, availabilityPercent: number|null }>}
 */
export function parseNoDataStatsByAssetId(apiResponse) {
  const rows = Array.isArray(apiResponse)
    ? apiResponse
    : apiResponse?.Assets
      ?? apiResponse?.AssetStats
      ?? apiResponse?.Stats
      ?? [];

  const map = new Map();
  for (const row of rows) {
    const assetId = row?.AssetId ?? row?.assetId;
    if (assetId == null || assetId === "") continue;

    const noDataEventCount = finiteNumber(
      row.NoDataEventCount ?? row.noDataEventCount
    );
    const availabilityPercent = finiteNumber(
      row.AvailabilityPercent ?? row.availabilityPercent
    );

    map.set(String(assetId), {
      noDataEventCount,
      availabilityPercent,
    });
  }

  return map;
}

/**
 * @param {Object[]} rows
 * @param {Map<string, { noDataEventCount: number|null, availabilityPercent: number|null }>} statsByAssetId
 * @param {{ idField?: string, availabilityField?: string }} [options]
 * @returns {Object[]}
 */
export function enrichRowsWithNoDataStats(
  rows,
  statsByAssetId,
  { idField = "id", availabilityField = "availabilityPercent" } = {}
) {
  if (!Array.isArray(rows)) return [];
  if (!statsByAssetId?.size) {
    return rows.map((row) => ({
      ...row,
      noDataEventCount: null,
      [availabilityField]: null,
    }));
  }

  return rows.map((row) => {
    const stats = statsByAssetId.get(String(row[idField]));
    return {
      ...row,
      noDataEventCount: stats?.noDataEventCount ?? null,
      [availabilityField]: stats?.availabilityPercent ?? null,
    };
  });
}

/**
 * @param {Object[]} assets
 * @param {Map<string, { noDataEventCount: number|null, availabilityPercent: number|null }>} statsByAssetId
 * @returns {Object[]}
 */
export function enrichAssetsWithNoDataStats(assets, statsByAssetId) {
  return enrichRowsWithNoDataStats(assets, statsByAssetId, { idField: "id" });
}

/**
 * @param {number|null|undefined} count
 * @returns {string}
 */
export function formatNoDataEventCount(count) {
  if (count == null || !Number.isFinite(count)) return "—";
  return String(count);
}

/**
 * @param {number|null|undefined} percent
 * @returns {string}
 */
export function formatAvailabilityPercent(percent) {
  if (percent == null || !Number.isFinite(percent)) return "—";
  return `${percent.toFixed(1)}%`;
}

/**
 * @param {number|null|undefined} percent
 * @param {boolean} isLoading
 * @returns {string}
 */
export function formatAvailabilityPercentWhileLoading(percent, isLoading) {
  if (isLoading && percent == null) return "—";
  return formatAvailabilityPercent(percent);
}

/**
 * @param {number|null|undefined} percent
 * @param {number} [decimals]
 * @returns {string}
 */
export function formatPercentForCsv(percent, decimals = 2) {
  if (percent == null || !Number.isFinite(percent)) return "";
  return percent.toFixed(decimals);
}

/**
 * @param {number} silentMs
 * @returns {string}
 */
export function silentMsToDays(silentMs) {
  if (!Number.isFinite(silentMs) || silentMs <= 0) return "0.00";
  return (silentMs / MS_PER_DAY).toFixed(2);
}

/**
 * @param {unknown} statsApiResponse
 * @param {string|number|null|undefined} assetId
 * @param {number|null|undefined} [fallbackPercent]
 * @returns {number|null}
 */
export function resolveCommunicationsAvailabilityPercent(
  statsApiResponse,
  assetId,
  fallbackPercent
) {
  const fromFetch = parseNoDataStatsByAssetId(statsApiResponse).get(
    String(assetId)
  )?.availabilityPercent;
  return finiteNumber(fromFetch) ?? finiteNumber(fallbackPercent);
}

const SORTABLE_COLUMNS = [
  "assetNumber",
  "vendor",
  "location",
  "customer",
  "telemetrySource",
  "lastUpdated",
  "noDataEventCount",
  "availabilityPercent",
  "silentDurationMs",
  "severity",
];

/**
 * @param {Object[]} assets
 * @param {string|null} sortColumn
 * @param {string} sortDirection
 * @param {Date} now
 * @returns {Object[]}
 */
export function sortNoDataAssets(assets, sortColumn, sortDirection, now) {
  const enriched = assets.map((a) => enrichAssetWithSilentFields(a, now));
  const dir = sortDirection === "asc" ? 1 : -1;

  const column = SORTABLE_COLUMNS.includes(sortColumn)
    ? sortColumn
    : "silentDurationMs";

  return [...enriched].sort((a, b) => {
    let aVal;
    let bVal;

    if (column === "silentDurationMs") {
      aVal = a.silentDurationMs;
      bVal = b.silentDurationMs;
    } else if (column === "severity") {
      const order = { warn: 1, critical: 2, critical30d: 3 };
      aVal = order[a.severity?.key] ?? 0;
      bVal = order[b.severity?.key] ?? 0;
    } else if (column === "lastUpdated") {
      aVal = new Date(a.lastUpdated || 0).getTime();
      bVal = new Date(b.lastUpdated || 0).getTime();
    } else if (
      column === "noDataEventCount"
      || column === "availabilityPercent"
    ) {
      aVal = Number.isFinite(a[column]) ? a[column] : -1;
      bVal = Number.isFinite(b[column]) ? b[column] : -1;
    } else if (column === "customer") {
      aVal = a.customer || a.lease || "";
      bVal = b.customer || b.lease || "";
      const cmp = String(aVal).localeCompare(String(bVal));
      return dir * cmp;
    } else {
      aVal = a[column] ?? "";
      bVal = b[column] ?? "";
      const cmp = String(aVal).localeCompare(String(bVal));
      return dir * cmp;
    }

    if (aVal < bVal) return -1 * dir;
    if (aVal > bVal) return 1 * dir;
    return 0;
  });
}

/**
 * @param {Date} endCutoff
 * @returns {string}
 */
export function formatCutoffDisplay(endCutoff) {
  if (!endCutoff || isNaN(endCutoff.getTime())) return "";
  return endCutoff.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}
